import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Announcement, announcementTypes } from '@resy/ui-kitchen';
import Logger from '../../../../../ui-kitchen/src/utils/logger';
import {
  getAnnouncements, postAnnouncementViewed,
} from './AnnouncementAPI';
import { getUuid } from '../../../../../ui-kitchen/src/utils';
import { updateContentTileThunk, resetContentTileThunk } from '../../../../../ui-kitchen/src/reducers/announcementsSlice';
import { selectIsAuthenticated } from '../../../../../ui-kitchen/src/reducers/authTokenSlice';

const { HEADER, POPUP, CONTENT_TILE } = announcementTypes;

const formatAnnouncement = (response) => {
  const {
    id,
    display: {
      heading,
      title,
      content,
      action_text: primaryLinkText,
      secondary_action_text: secondaryLinkText,
      image,
      web: { type },
      color_dict: colorDict,
      show_remind_me_later: showRemindMeLater = false,
      remind_me_later_count: remindMeLaterCount = 0,
    },
    action: {
      data: {
        operation,
        url: primaryLinkUrl,
      },
    },
    // eslint-disable-next-line camelcase
    secondary_action,
  } = response;

  const colors = {
    [POPUP]: {
      heading: colorDict.header_text,
      title: colorDict.title_text,
      font: colorDict.content_text,
      background: colorDict.background,
      primaryButton: {
        background: colorDict.button,
        font: colorDict.button_text,
      },
      secondaryButton: {
        background: colorDict.secondary_button,
        font: colorDict.secondary_button_text,
      },
    },
    [HEADER]: {
      background: colorDict.background,
      font: colorDict.title_text,
    },
  };

  return {
    id,
    colors,
    content,
    heading,
    image,
    operation,
    primaryLinkText,
    primaryLinkUrl,
    secondaryLinkText,
    secondaryLinkUrl: secondary_action?.data.url,
    showRemindMeLater,
    remindMeLaterCount,
    title,
    type,
  };
};

const AnnouncementContainer = ({
  $rootScope,
  Analytics,
  locationCode,
  deviceId: uuid,
  updateContentTile,
  resetContentTile,
  isAuthenticated,
}) => {
  const [deviceId, setDeviceId] = useState(null);
  const [announcement, setAnnouncement] = useState({});

  const transformAnnouncement = (item) => ({
    image: item.display?.image || ' ',
    tag: {
      name: item.display?.heading || ' ',
      url: item.action?.data?.url,
    },
    title: item.display?.title || ' ',
    subtitle: item.display?.content || ' ',
    url: item.action?.data?.url,
    id: item.id,
    isContentTile: true,
  });

  const markAnnouncementViewed = (shouldRemindMeLater = false) => {
    if (document.announcementTimer) {
      clearTimeout(document.announcementTimer);
    }

    document.announcementTimer = setTimeout(async () => {
      try {
        await postAnnouncementViewed({
          id: announcement.id,
          deviceId,
          ...(shouldRemindMeLater && { remindMeLater: true }),
        });
      } catch (err) {
        Logger.error(err);
      }
    }, 1500);
  };

  useEffect(() => {
    const [newDeviceId, listener] = getUuid(uuid);
    setDeviceId(newDeviceId);

    return () => {
      listener();
    };
  }, [uuid]);

  useEffect(() => {
    const fetchAnnouncement = async () => {
      if (deviceId && locationCode) {
        try {
          const response = await getAnnouncements({ deviceId, locationCode });
          if (response) {
            const contentTile = response.find((item) => item.display?.web?.type === CONTENT_TILE);
            if (contentTile) {
              const post = transformAnnouncement(contentTile);
              updateContentTile(post);
            } else {
              resetContentTile();
            }

            const popUpAnnouncement = response.find((item) => item.display?.web?.type === POPUP);
            if (popUpAnnouncement) {
              const formattedAnnouncement = formatAnnouncement(popUpAnnouncement);
              setAnnouncement(formattedAnnouncement);
            }
          }
        } catch (err) {
          Logger.error(err);
        }
      }
    };

    fetchAnnouncement();
  }, [deviceId, locationCode, isAuthenticated, updateContentTile, resetContentTile]);

  const handleEvent = (name, attrs) => {
    Analytics.event(name, {
      ...attrs,
      category: 'UI Interaction',
    });
  };

  if (!announcement.id) {
    return null;
  }

  return (
    <Announcement
      id={announcement.id}
      heading={announcement.heading}
      title={announcement.title}
      content={announcement.content}
      primaryLinkUrl={announcement.primaryLinkUrl}
      primaryLinkText={announcement.primaryLinkText}
      secondaryLinkText={announcement.secondaryLinkText}
      secondaryLinkUrl={announcement.secondaryLinkUrl}
      operation={announcement.operation}
      event={handleEvent}
      colors={announcement.colors}
      image={announcement.image}
      type={announcement.type}
      showRemindMeLater={announcement.showRemindMeLater}
      remindMeLaterCount={announcement.remindMeLaterCount}
      markAnnouncementViewed={markAnnouncementViewed}
      $rootScope={$rootScope}
    />
  );
};

AnnouncementContainer.propTypes = {
  $rootScope: PropTypes.shape({}).isRequired,
  Analytics: PropTypes.shape({
    event: PropTypes.func,
  }).isRequired,
  locationCode: PropTypes.string,
  deviceId: PropTypes.string.isRequired,
  updateContentTile: PropTypes.func.isRequired,
  resetContentTile: PropTypes.func.isRequired,
  isAuthenticated: PropTypes.bool,
};

AnnouncementContainer.defaultProps = {
  locationCode: null,
  isAuthenticated: false,
};

export const mapStateToProps = (state) => ({
  locationCode: state.resyLocations.selected?.code,
  deviceId: state.resyConfig.deviceId,
  isAuthenticated: selectIsAuthenticated(state),
});

const mapDispatchToProps = {
  updateContentTile: updateContentTileThunk,
  resetContentTile: resetContentTileThunk,
};

AnnouncementContainer.displayName = 'AnnouncementContainer';

export { AnnouncementContainer as ComponentUnderTest };
export default connect(mapStateToProps, mapDispatchToProps)(AnnouncementContainer);
